/**
 * @file: useAsyncResource.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { InstanceOrFactory } from '@euler/typings';
import { makeDebug } from '@euler/utils/debug';
import { safeMarkDevFlag } from '@euler/utils/safeMarkDevFlag';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Subject } from 'rxjs';
import { onErrorIgnore } from '../onErrorIgnore';

const debug = makeDebug('async-resource');

type State<T> =
  | {
      status: 'pending';
    }
  | {
      status: 'loading';
      promise: Promise<T>;
    }
  | {
      status: 'error';
      error?: any;
    }
  | {
      status: 'loaded';
      value: T;
    };

const cache = new Map<any, AsyncResource<any>>();
const kDebug = safeMarkDevFlag(false);

if (kDebug) {
  setInterval(() => {
    debug('>>>>>>>>>>> async resource count: %d, resources: %O', cache.size, [
      ...cache.keys(),
    ]);
  }, 5000);
}

export class AsyncResource<T> {
  refreshed = false;
  readonly update$ = new Subject<T>();

  private state: State<T> = { status: 'pending' };

  constructor(private readonly factory: () => Promise<T>) {}

  read(initialValue?: T) {
    if (this.state.status === 'loaded') {
      return this.state.value;
    }

    if (this.state.status === 'error') {
      throw this.state.error;
    }

    if (this.state.status === 'loading') {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw this.state.promise;
    }

    if (initialValue !== undefined) {
      return initialValue;
    }

    const promise = this.factory();

    promise.then(
      value => {
        this.state = {
          status: 'loaded',
          value,
        };
      },
      error => {
        this.state = {
          status: 'error',
          error,
        };
      },
    );

    this.refreshed = true;

    this.state = {
      status: 'loading',
      promise,
    };

    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw promise;
  }

  resetError() {
    if (this.state.status === 'error') {
      this.state = { status: 'pending' };
    }
  }

  update(value: T) {
    this.state = { status: 'loaded', value };
  }
}

export const useClearAsyncResource = () => {
  return useCallback((key: string) => {
    cache.delete(key);
    debug('cleared async resource %s', key);
  }, []);
};

export const useResetAsyncResourceError = () => {
  return useCallback((key: string) => {
    cache.get(key)?.resetError();
  }, []);
};

export const useAutoClearAsyncResource = <T>(
  factory: () => Promise<T>,
  key: string,
  initialValue?: T,
) => {
  return useAsyncResource(factory, key, initialValue, { autoClear: true });
};

export const useAsyncResource = <T>(
  factory: () => Promise<T>,
  key: string,
  initialValue?: T,
  options?: { autoClear?: boolean },
) => {
  if (!key) {
    throw new Error('Async resource key cannot be empty or null');
  }
  const resource = useRef<AsyncResource<T>>();
  useMemo(() => {
    if (cache.has(key)) {
      resource.current = cache.get(key);
      resource.current?.resetError();
    } else {
      resource.current = new AsyncResource(factory);
      cache.set(key, resource.current);
    }
  }, [factory, key]);
  const result = resource.current!.read(initialValue);
  const [value, setValue] = useState(result);
  const refreshed = useRef(false);
  useEffect(() => {
    if (resource.current?.refreshed) {
      resource.current.refreshed = false;
    } else if (!resource.current?.refreshed && !refreshed.current) {
      refreshed.current = true;
      factory().then(setValue).catch(onErrorIgnore);
    }
    return () => {
      resource.current?.resetError();
    };
  }, [key, factory]);
  const autoClear = options?.autoClear;
  const clear = useClearAsyncResource();
  useEffect(
    () => () => {
      if (autoClear !== false) {
        clear(key);
      }
    },
    [autoClear, clear, key],
  );
  return value;
};

export const useConditionalAsyncResource = <T>(
  factory: () => Promise<T>,
  key: string | null | undefined,
  initialValue?: T,
  options?: { autoClear?: boolean },
) => {
  const resource = useRef<AsyncResource<T>>();
  useMemo(() => {
    if (key) {
      if (cache.has(key)) {
        resource.current = cache.get(key);
        resource.current?.resetError();
      } else {
        resource.current = new AsyncResource(factory);
        cache.set(key, resource.current);
      }
    }
  }, [factory, key]);
  const result = resource.current?.read(initialValue);
  const [value, setValue] = useState(key ? result : initialValue);
  const refreshed = useRef(false);
  useEffect(() => {
    if (!resource.current) return;
    if (resource.current?.refreshed) {
      resource.current.refreshed = false;
    } else if (!resource.current?.refreshed && !refreshed.current) {
      refreshed.current = true;
      factory().then(setValue).catch(onErrorIgnore);
    }
    return () => {
      resource.current?.resetError();
    };
  }, [key, factory]);
  const autoClear = options?.autoClear;
  const clear = useClearAsyncResource();
  useEffect(
    () => () => {
      if (autoClear !== false && key) {
        clear(key);
      }
    },
    [autoClear, clear, key],
  );
  return value;
};

export const useRefreshableAsyncResource = <T>(
  factory: () => Promise<T>,
  key: string,
  initialValue?: T,
  options?: { autoClear?: boolean },
) => {
  const resource = useRef<AsyncResource<T>>();
  useMemo(() => {
    if (resource.current) return;
    if (cache.has(key)) {
      resource.current = cache.get(key);
      resource.current?.resetError();
    } else {
      resource.current = new AsyncResource(factory);
      cache.set(key, resource.current);
    }
  }, [factory, key]);
  const result = resource.current!.read(initialValue);
  const [value, setValue] = useState(result);
  const [error, setError] = useState<Error>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshed = useRef(false);

  const refresh = useCallback(
    (silent?: boolean) => {
      if (!silent) setIsRefreshing(true);
      factory()
        .then(nextValue => {
          resource.current!.update(nextValue);
          resource.current!.update$.next(nextValue);
        })
        .catch(e => {
          setError(e as Error);
        })
        .finally(() => !silent && setIsRefreshing(false));
    },
    [factory],
  );

  useEffect(() => {
    const sub = resource.current!.update$.subscribe(nextValue => {
      setValue(nextValue);
      resource.current!.update(nextValue);
    });
    return () => sub.unsubscribe();
  });

  useEffect(() => {
    if (resource.current?.refreshed) {
      resource.current.refreshed = false;
    } else if (!resource.current?.refreshed && !refreshed.current) {
      refreshed.current = true;
      refresh(true);
    }
    return () => {
      resource.current?.resetError();
    };
  }, [key, factory, refresh]);

  const update = useCallback((valueOrFactory: InstanceOrFactory<T>) => {
    if (typeof valueOrFactory === 'function') {
      resource.current!.update$.next(valueOrFactory());
    } else {
      resource.current!.update$.next(valueOrFactory as any);
    }
  }, []);

  const autoClear = options?.autoClear;
  const clear = useClearAsyncResource();
  useEffect(
    () => () => {
      if (autoClear !== false) {
        clear(key);
      }
    },
    [autoClear, clear, key],
  );

  return { value, isRefreshing, refresh, update, error };
};
